import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import {
  deleteBasketItem,
  fetchBasket,
  fetchBasketItems,
  fetchBasketTotals,
  resetBasketItems,
  resetBasketTotals,
  updateBasketItemNotes,
  updateBasketItemQuantity,
  updateBasketItemStock,
} from "../../store/features/ordering/basket/basket-slice";
import {
  GoogleIcon,
  ICON_ID,
  ICON_SIZES,
} from "../../components/icons/google-icon";
import Button from "../../components/controls/button";
import styled from "styled-components";
import Image from "../../components/controls/image";
import {
  closeConfirm,
  openConfirm,
} from "../../store/features/notification/confirm-slice";
import { TableContainer } from "../../styles/shared/table";
import CardTableView, { CardTableViewConfig } from "../../components/controls/card-table-view";
import TextInput from "../../components/controls/text-input";
import { minValueValidator } from "../../helpers/validators";
import { currencyFormatter, palletFormatter } from "../../helpers/formatters";
import PalletBreakdown from "../../components/order/pallet-breakdown";
import NumberInput from "../../components/controls/number-input";
import { FetchParams } from "../../components/controls/table-view";
import LoadingSpinner from "../../components/controls/loading-spinner";

interface InlineFieldEdit {
  [basketItemId: number]: {
    validation: string | null
  };
}

export default function Basket() {
  const basket = useAppSelector((state) => state.basket);
  const selectedBranch = useAppSelector((state) => state.branch).selectedBranch;
  const dispatch = useAppDispatch();
  const [showSupplierBreakdown, setShowSupplierBreakdown] = useState(false);

  const [currentStockEdit, setCurrentStockEdit] = useState<InlineFieldEdit>({
    0: { validation: null },
  });

  const [quantityEdit, setQuantityEdit] = useState<InlineFieldEdit>({
    0: { validation: null },
  });

  const [notesEdit, setNotesEdit] = useState<InlineFieldEdit>({
    0: { validation: null },
  });


  useEffect(() => {
    if (basket.basket != null) {
      dispatch(fetchBasketItems({
        basketId: basket.basket.id, fetchParams: {
          reset: true
        }
      }));
      dispatch(fetchBasketTotals(basket.basket.id));
    } else if (selectedBranch) {
      dispatch(fetchBasket(selectedBranch.id));
    }
  }, [basket.basket, dispatch, selectedBranch]);

  useEffect(() => {
    if (basket.basketItems) {
      basket.basketItems.items.forEach(x => {
        const id = x.id;
        setCurrentStockEdit((prevState) => ({
          ...prevState,
          [id]: {
            value: x.currentStock,
            validation: null
          },
        }))
        setQuantityEdit((prevState) => ({
          ...prevState,
          [id]: {
            value: x.quantity,
            validation: null
          },
        }))
        setNotesEdit((prevState) => ({
          ...prevState,
          [id]: {
            value: x.notes,
            validation: null
          },
        }))
      })
    }
  }, [basket.basketItems])

  useEffect(() => {
    return () => {
      dispatch(resetBasketItems());
      dispatch(resetBasketTotals());
    }
  }, [dispatch])

  const fetchMoreData = async (fetchParams: FetchParams) => {
    if (basket.basket != null) {
      await dispatch(fetchBasketItems({ basketId: basket.basket.id, fetchParams }));
    }
  };

  const updateCurrentStock = (id: number, currentStock: number | null) => {
    const invalid = minValueValidator(currentStock, 0)
    setCurrentStockEdit((prevState) => ({
      ...prevState,
      [id]: {
        validation: invalid
      },
    }))

    if (!invalid) {
      dispatch(updateBasketItemStock({ basketItemId: id, currentStock: currentStock! }));
    }
  };

  const updateQuantity = (id: number, quantity: number | null) => {
    const row = basket.basketItems?.items.find(x => x.id === id);
    const invalid = minValueValidator(quantity, 0)
      ?? (row?.layerSize && quantity! % row.layerSize !== 0 ? `Quantity must be a multiple of ${row.layerSize} cases.` : null);
    setQuantityEdit((prevState) => ({
      ...prevState,
      [id]: {
        validation: invalid
      },
    }))

    if (!invalid) {
      dispatch(
        updateBasketItemQuantity({ basketItemId: id, quantity: quantity! })
      );
    }
  };

  const updateNotes = (id: number, notes: string) => {

    dispatch(
      updateBasketItemNotes({ basketItemId: id, notes: notes })
    );
  };


  const cardConfig: CardTableViewConfig = {
    render: (row: any) => {
      return (
        <CardContainer>
          <HeaderSection>
            <ProductImageWrapper>
              <Image image={row.productImage} />
            </ProductImageWrapper>
            <ProductInfo>
              <ProductTitle>{row.productName}</ProductTitle>
              <ProductDescription>
                {row.size} {row.sizeUnit} – {row.productDescription}
              </ProductDescription>
              <SmallText>{row.supplierName}</SmallText>
              <ProductPrice>
                {row.openPricing ? <SmallText>Cost Price: TBC</SmallText> : (
                  <>
                    <SmallText>Cost Price: {currencyFormatter.format(row.costPrice)}</SmallText>
                    <SmallText>({currencyFormatter.format(row.costPrice! / row.size)} per {row.sizeUnit})</SmallText>
                  </>
                )}
              </ProductPrice>
            </ProductInfo>
            <DeleteContainer>
              <Button
                variant="text"
                id={row.Name}
                label=""
                icon={ICON_ID.Delete}
                isDanger={true}
                fullWidth={false}
                onClick={() => handleDeleteClick(row)}
              />
            </DeleteContainer>
          </HeaderSection>

          <InputGrid>
            <NumberInputs>
              <NumberInput
                label="In stock"
                id={`stock-${row.id}`}
                 min={0}
                value={row.currentStock}
                error={currentStockEdit[row.id]?.validation}
                onChange={(value) => updateCurrentStock(row.id, value)}
              />

              <NumberInput
                label="Quantity"
                id={`quantity-${row.id}`}
                value={row.quantity}
                step={row.layerSize ?? 1}
                min={row.layerSize ?? 0}
                error={quantityEdit[row.id]?.validation}
                onChange={(value) => updateQuantity(row.id, value)}
              />
              {row.layerSize && row.palletEquivalent != null && (
                <PalletLineDetail>
                  {row.quantity} cases · {row.layersOrdered ?? row.quantity / row.layerSize} layers · {palletFormatter(row.palletEquivalent)} pallets
                </PalletLineDetail>
              )}
            </NumberInputs>
            <NoteInput>
              <TextInput
                label="Notes"
                type="text"
                id={`notes-${row.id}`}
                value={row.notes}
                error={notesEdit[row.id]?.validation}
                multiline={true}
                onChange={(event) => updateNotes(row.id, event.target.value)}
                sx={{ marginTop: "0px", marginBottom: "0px" }}
              />
            </NoteInput>
          </InputGrid>
        </CardContainer >
      );
    },
  };

  const handleDeleteClick = (row: any) => {
    const onConfirm = () => {
      dispatch(deleteBasketItem(row.id));
      dispatch(closeConfirm());
    };

    const onCancel = () => {
      dispatch(closeConfirm());
    };

    dispatch(
      openConfirm({
        title: "Remove " + row.productName + "?",
        message:
          "Are you sure you want to remove this product from your basket?",
        show: true,
        data: {
          onConfirm: onConfirm,
          onCancel: onCancel,
        },
      })
    );
  };



  return (
    <TableContainer>
      <BasketSummaryRow>
        <BasketTotalsCard>
          <BasketTotalsHeader>
            <BasketTotalsText>
              <BasketTotalsLabel>Order totals</BasketTotalsLabel>
              {basket.basketTotalsLoading ? (
                <LoadingContainer>
                  <LoadingSpinner />
                </LoadingContainer>
              ) : (
                <BasketTotalsValues>
                  <BasketTotalsValue>
                    <span>Items</span>
                    <strong>{basket.basketTotals?.totalItemCount ?? 0}</strong>
                  </BasketTotalsValue>
                  <BasketTotalsValue>
                    <span>Total</span>
                    <strong>{currencyFormatter.format(basket.basketTotals?.totalValue ?? 0)}</strong>
                  </BasketTotalsValue>
                </BasketTotalsValues>
              )}
            </BasketTotalsText>

            <ExpandButton
              type="button"
              onClick={() => setShowSupplierBreakdown((prevState) => !prevState)}
              disabled={!basket.basketTotals?.suppliers?.length}
              aria-expanded={showSupplierBreakdown}
              aria-label="Toggle supplier totals"
            >
              <GoogleIcon
                id={showSupplierBreakdown ? ICON_ID.ExpandUp : ICON_ID.ExpandDown}
                size={ICON_SIZES.Medium}
                isPrimary={!basket.basketTotals?.suppliers?.length}
                isNeutral={!!basket.basketTotals?.suppliers?.length}
              />
            </ExpandButton>
          </BasketTotalsHeader>

          {basket.basketTotalsError && (
            <TotalsErrorText>{basket.basketTotalsError}</TotalsErrorText>
          )}

          {showSupplierBreakdown && !!basket.basketTotals?.suppliers?.length && (
            <SupplierBreakdownList>
              {basket.basketTotals.suppliers.map((supplier) => (
                <SupplierBreakdownItem key={supplier.supplierId}>
                  <SupplierHeading>{supplier.supplierName}</SupplierHeading>
                  <SupplierMeta>
                    <span>{supplier.itemCount} items</span>
                    <strong>{currencyFormatter.format(supplier.totalValue)}</strong>
                  </SupplierMeta>
                </SupplierBreakdownItem>
              ))}
            </SupplierBreakdownList>
          )}
          <PalletBreakdown suppliers={basket.basketTotals?.suppliers ?? []} />
        </BasketTotalsCard>
      </BasketSummaryRow>

      <CardTableView
        id="basketTable"
        emptyText="Your basket is empty"
        fetchMoreData={fetchMoreData}
        totalCount={basket.basketItems?.count ?? 0}
        data={basket.basketItems?.items ?? []}
        cardConfig={cardConfig}
        preLoadedData={true}
        headerText={"Basket"}
        search={true}
      />
    </TableContainer>
  );
}


const CardContainer = styled.div`
  background-color: white;
  padding: 16px;
  gap: 5px;
  justify-content: space-between;
  display:flex;
  flex-direction:row;
  flex-wrap:wrap;
  border-bottom: 1px solid ${props => props.theme.application.border};    
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: flex-start;
  flex:1;
  gap:10px;
`;

const ProductImageWrapper = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 8px;
`;

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width:100%;
  gap:3px;
`;

const ProductPrice = styled.div`
display:flex;
gap:5px;
flex-wrap:wrap;
`

const NoteInput = styled.div`
margin-top:10px;
flex-grow:1;
`
const NumberInputs = styled.div`
display:flex;
width:100%;
align-items:flex-end;
gap:5px;
`
const ProductTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
`;

const ProductDescription = styled.p`
  font-size: 0.9rem;
  color: #555;
  margin: 4px 0;
`;

const SmallText = styled.p`
  font-size: 1rem;
  margin: 0;
  font-weight:bold;
`;

const InputGrid = styled.div`
  display: flex;
  flex-direction:row;
  align-items:flex-end;
  flex-wrap:wrap;
  flex:1;
  margin-top:5px;
`;

const DeleteContainer = styled.div`
  button {
    span {
      font-size: 1.5rem;
    }
  }
`;

const BasketSummaryRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const BasketTotalsCard = styled.div`
  width: 100%;
  max-width: 420px;
  background-color: white;
  border: 1px solid ${(props) => props.theme.application.border};
  padding: 16px;

  @media (min-width: 700px) {
    width: 420px;
  }
`;

const BasketTotalsHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const BasketTotalsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const BasketTotalsLabel = styled.span`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${(props) => props.theme.application.text};
`;

const BasketTotalsValues = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const BasketTotalsValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 0.85rem;
    color: #555;
  }

  strong {
    font-size: 1.15rem;
  }
`;

const ExpandButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  span {
    margin-right: 0;
  }
`;

const SupplierBreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => props.theme.application.border};
`;

const SupplierBreakdownItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
`;

const SupplierHeading = styled.span`
  font-weight: 600;
`;

const SupplierMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: right;

  span {
    color: #555;
  }
`;

const LoadingContainer = styled.div`
  justify-content: flex-start;

  div {
    justify-content: flex-start;
  }

  span {
    margin-left: 12px;
  }
`;

const TotalsErrorText = styled.div`
  margin-top: 12px;
  color: #d32f2f;
`;

const PalletLineDetail = styled.small`
  display: block;
  width: 100%;
  color: #555;
`;

